import { getFocusableElements } from "../../../../common/utils/dom.ts";
import { focusWithSetTabIndex } from "../../../../common/utils/focus.ts";

function getToastItem(container: HTMLElement, toastId: string) {
  return Array.from(container.children).find(
    (item) => item.getAttribute("data-toast-id") === toastId
  );
}

function focusToast(container: HTMLElement, toastId: string) {
  const item = getToastItem(container, toastId);
  if (!(item instanceof HTMLElement)) {
    return false;
  }
  const [firstFocusable] = getFocusableElements(item);
  if (!firstFocusable) {
    return false;
  }
  firstFocusable.focus();
  return true;
}

export function toastHasFocus(container: HTMLElement | null, toastId: string) {
  if (!container) {
    return false;
  }
  return Boolean(
    getToastItem(container, toastId)?.contains(document.activeElement)
  );
}

/**
 * Moves focus to the remaining toast as they're closed, in order. When none
 * is left, returns focus to the opener that pulled focus into the region,
 * falling back to the main content when there is no (longer a) usable opener.
 */
export function handOffToastFocus(
  container: HTMLElement | null,
  openToastIds: string[],
  closedToastId: string,
  opener: HTMLElement | null,
  openerScope: HTMLElement | null
) {
  if (container) {
    const index = openToastIds.indexOf(closedToastId);
    const neighborIds =
      index < 0
        ? []
        : [
            ...openToastIds.slice(index + 1),
            ...openToastIds.slice(0, index).reverse(),
          ];
    if (neighborIds.some((toastId) => focusToast(container, toastId))) {
      return;
    }
  }
  if (opener?.isConnected) {
    opener.focus();
    if (document.activeElement === opener) {
      return;
    }
  }
  // An opener replaced while a toast has focus, e.g. by revalidation, can name
  // a stand-in within its scope
  const dest = openerScope?.isConnected
    ? openerScope.querySelector<HTMLElement>("[data-focus-dest]")
    : null;
  if (dest) {
    dest.focus();
    if (document.activeElement === dest) {
      return;
    }
  }
  const main = document.querySelector<HTMLElement>("main");
  if (main) {
    focusWithSetTabIndex(main, { preventScroll: true });
  }
}
